import WebFooterModel from '@/models/core-site/WebFooterModel';
import { awaitBuilderData } from '../builder';
import { getAllCategories } from '../helpers/BuilderHelper';
import { Locale, LOCALE_LINKS, standardizeLocale } from '../helpers/LangHelper';
import { fetchGlobalNavItems } from '../helpers/NavHelper';
import BusinessIndustriesModel from '../models/BusinessIndustries';
import { BusinessHubMenuItemType, BusinessIndustries } from '../types/BusinessIndustries';

export const getCommonPageDetails = async (pageSlug: string, locale?: Locale, builderPage?: any) => {
    if (!locale) {
        locale = Locale.Australia;
    }

    const standardLocale = standardizeLocale(locale);
    const canonicalUrl = `${LOCALE_LINKS[standardLocale]?.host}/${standardLocale}${pageSlug}`;

    const footerItems = await WebFooterModel.getPageFooter(standardLocale);
    const categories = await getAllCategories('id,name,data.isSeries,data.longName');

    if (builderPage) {
        await awaitBuilderData(builderPage, locale);
    }

    return {
        footerItems,
        categories,
        canonicalUrl,
    };
};

export const getBusinessHubMenu = async (locale?: Locale): Promise<{ menuData: BusinessHubMenuItemType[] }> => {
    if (!locale) {
        locale = Locale.Australia;
    }

    const standardLocale = standardizeLocale(locale);
    const res = await BusinessIndustriesModel.getAllIndustries({ locale: standardLocale, includeUnpublished: false });

    const industries = res
        .filter((item) => item.published === 'published' || (item.data as BusinessIndustries).isInPreview)
        .map((item) => item.data as BusinessIndustries)
        .sort((a, b) => (b.order || 0) - (a.order || 0))
        .map((data) => ({
            name: data.name,
            key: `industries-${data.name}`,
            url: (data.builderPage?.value?.data as any)?.url,
            isInPreview: data.isInPreview,
        }));

    return {
        menuData: [
            {
                name: 'Home',
                key: 'home',
                url: '/business',
            },
            {
                name: 'Products',
                key: 'product',
                url: '/business/products',
            },
            {
                name: 'Partners',
                key: 'partners',
                url: '/business/partners',
                locale: [Locale.USA, Locale.CanadaEnglish, Locale.GreatBritain],
            },
            {
                name: 'Industries',
                key: 'industries',
                childrenItems: industries,
                locale: [Locale.Australia, Locale.NewZealand],
            },
            {
                name: 'Larger businesses',
                key: 'larger-businesses',
                url: '/business/larger-businesses',
                locale: [Locale.Australia, Locale.NewZealand],
            },
            {
                name: 'Franchise',
                key: 'franchise',
                url: '/business/franchise',
                locale: [Locale.Australia, Locale.NewZealand],
            },
            {
                name: 'Resources',
                key: 'business-resources',
                url: '/business/resources',
                locale: [Locale.Australia, Locale.NewZealand, Locale.USA, Locale.CanadaEnglish],
                childrenItems: [
                    {
                        name: 'Afterpay Success Stories',
                        key: 'resource_afterpay_success_stories',
                        url: '/business/access/case-studies',
                        locale: [Locale.Australia, Locale.NewZealand, Locale.USA, Locale.CanadaEnglish],
                    },
                    {
                        name: 'Developer/API Documentation',
                        key: 'resource_developer_api_documentation',
                        url: 'https://developers.afterpay.com/docs/api/welcome/getting-started',
                        locale: [Locale.Australia, Locale.NewZealand, Locale.USA, Locale.CanadaEnglish],
                    },
                    {
                        name: 'Industry Partners',
                        key: 'resource_industry_partners',
                        url: '/business/resources/industry-partners',
                        locale: [Locale.Australia],
                    },
                    {
                        name: 'Marketing Resources',
                        key: 'resource_marketing_resources',
                        url: '/business/resources/marketing',
                        locale: [Locale.Australia, Locale.NewZealand, Locale.USA, Locale.CanadaEnglish],
                    },
                    {
                        name: 'Platform Integrations',
                        key: 'resource_platform_integrations',
                        url: '/business/platform-integrations',
                        locale: [Locale.Australia, Locale.NewZealand],
                    },
                    // {
                    //     name: 'Platform Partners',
                    //     key: 'resource_platform_partners',
                    //     url: '/business/platform-partner',
                    //     locale: [Locale.Australia, Locale.NewZealand],
                    // },
                    {
                        name: 'Trade Events',
                        key: 'resource_trade_events',
                        url: '/business/trade-activity',
                        locale: [Locale.Australia, Locale.NewZealand],
                    },
                    {
                        name: 'Training',
                        key: 'resource_training',
                        url: '/business/resources/training',
                        locale: [Locale.Australia, Locale.NewZealand],
                    },
                    {
                        name: 'Visual Merchandise',
                        key: 'resource_visual_merchandise',
                        url: '/business/resources/marketing/in-store',
                        locale: [Locale.Australia, Locale.NewZealand],
                    },
                ],
            },
            {
                name: 'Resources',
                key: 'business-resources-gb',
                url: '/business/resources',
                locale: [Locale.GreatBritain],
                childrenItems: [
                    {
                        name: 'Marketing Resources',
                        key: 'resource_marketing_resources',
                        url: '/business/resources/marketing',
                        locale: [Locale.GreatBritain],
                    },
                    {
                        name: 'Developer/API Documentation',
                        key: 'resource_developer_api_documentation',
                        url: 'https://developers.clearpay.co.uk/clearpay-online/docs/getting-started-with-clearpay-online',
                        locale: [Locale.GreatBritain],
                    },
                    {
                        name: 'Platform Integrations',
                        key: 'resource_platform_integrations',
                        url: '/business/partners',
                        locale: [Locale.GreatBritain],
                    },
                ],
            },
        ],
    };
};
